/**
 * Validacion y normalizacion de los enlaces de video (seccion 7.2).
 *
 * CaliPeak no descarga ni aloja video (RF-VID-05 / RF-LEG-05): solo guarda el
 * identificador del post y lo muestra con el reproductor embebido oficial.
 */

import type { Platform } from '@/types/domain';

/** Dominios admitidos (RF-VID-01). Cualquier otro se rechaza. */
const TIKTOK_HOSTS = ['tiktok.com', 'www.tiktok.com', 'm.tiktok.com', 'vm.tiktok.com', 'vt.tiktok.com'];
const INSTAGRAM_HOSTS = ['instagram.com', 'www.instagram.com', 'm.instagram.com'];

/** Rutas de Instagram que contienen video (RF-VID-01). */
const INSTAGRAM_PATHS = ['reel', 'reels', 'p', 'tv'];

/** Parametros de seguimiento que se eliminan al normalizar (RF-VID-02). */
const TRACKING_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'igsh',
  'igshid',
  'img_index',
  'is_from_webapp',
  'sender_device',
  'sender_web_id',
  'web_id',
  'refer',
  'referer_url',
  'referer_video_id',
  '_r',
  '_t',
  'fbclid',
  'gclid',
  'share_app_id',
  'share_link_id',
  'timestamp',
  'checksum',
  'social_sharing',
];

export type VideoUrlErrorCode =
  | 'EMPTY'
  | 'MALFORMED'
  | 'UNSUPPORTED_DOMAIN'
  | 'UNSUPPORTED_PATH'
  | 'MISSING_POST_ID'
  | 'SHORTLINK_UNRESOLVED'
  /** Historia normal: caduca a las 24 h, antes de que de tiempo a revisarla. */
  | 'STORY_EXPIRES'
  /** Destacada entera, sin el clip concreto. */
  | 'STORY_CLIP_MISSING';

export interface ParsedVideoUrl {
  platform: Platform;
  /** URL canonica, sin parametros de seguimiento. */
  normalizedUrl: string;
  /** Identificador del post, clave de la deteccion de duplicados (RF-VID-04). */
  postExternalId: string;
  /** Un enlace corto (vm.tiktok.com) necesita resolverse en servidor. */
  isShortLink: boolean;
  /** Autor extraido de la URL cuando la plataforma lo incluye. */
  authorHandle: string | null;
  /**
   * Historia de Instagram guardada en una destacada. No tiene reproductor
   * embebido ni oEmbed: no se previsualiza ni se comprueba que sea publica, y
   * el revisor la abre en Instagram.
   */
  isStory: boolean;
}

export interface VideoUrlResult {
  ok: boolean;
  value?: ParsedVideoUrl;
  error?: VideoUrlErrorCode;
}

/**
 * Analiza y normaliza el enlace pegado por el atleta.
 * No comprueba que la publicacion sea publica: eso exige una peticion de red y
 * lo hace el servidor (RF-VID-03).
 */
export function parseVideoUrl(rawInput: string): VideoUrlResult {
  const raw = rawInput?.trim();
  if (!raw) return { ok: false, error: 'EMPTY' };

  // El atleta suele pegar el enlace sin esquema desde la app movil.
  const withScheme = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;

  let url: URL;
  try {
    url = new URL(withScheme);
  } catch {
    return { ok: false, error: 'MALFORMED' };
  }

  const host = url.hostname.toLowerCase();

  if (TIKTOK_HOSTS.includes(host)) return parseTikTok(url, host);
  if (INSTAGRAM_HOSTS.includes(host)) return parseInstagram(url);

  return { ok: false, error: 'UNSUPPORTED_DOMAIN' };
}

function parseTikTok(url: URL, host: string): VideoUrlResult {
  const segments = url.pathname.split('/').filter(Boolean);

  // Enlace corto: https://vm.tiktok.com/ZGJxxxxx/
  if (host === 'vm.tiktok.com' || host === 'vt.tiktok.com') {
    const code = segments[0];
    if (!code) return { ok: false, error: 'MISSING_POST_ID' };
    return {
      ok: true,
      value: {
        platform: 'TIKTOK',
        normalizedUrl: `https://${host}/${code}`,
        // Provisional: la Edge Function lo sustituye por el id real al
        // resolver la redireccion (RF-VID-02).
        postExternalId: `tiktok:short:${code}`,
        isShortLink: true,
        authorHandle: null,
        isStory: false,
      },
    };
  }

  // Enlace completo: https://www.tiktok.com/@usuario/video/7361234567890123456
  const videoIndex = segments.indexOf('video');
  const id = videoIndex >= 0 ? segments[videoIndex + 1] : undefined;
  const handle = segments.find((s) => s.startsWith('@'))?.slice(1) ?? null;

  if (!id || !/^\d{6,}$/.test(id)) {
    return { ok: false, error: videoIndex >= 0 ? 'MISSING_POST_ID' : 'UNSUPPORTED_PATH' };
  }

  return {
    ok: true,
    value: {
      platform: 'TIKTOK',
      normalizedUrl: handle
        ? `https://www.tiktok.com/@${handle}/video/${id}`
        : `https://www.tiktok.com/video/${id}`,
      postExternalId: `tiktok:${id}`,
      isShortLink: false,
      authorHandle: handle,
      isStory: false,
    },
  };
}

/** Base64 (normal o apto para URL, con o sin relleno) a texto; null si no lo es. */
function decodeBase64(value: string): string | null {
  try {
    const standard = value.replace(/-/g, '+').replace(/_/g, '/');
    return atob(standard.padEnd(Math.ceil(standard.length / 4) * 4, '='));
  } catch {
    return null;
  }
}

/**
 * Historias de Instagram.
 *
 * Solo se admiten las guardadas en una destacada y que apuntan a un clip
 * concreto. Una historia normal desaparece a las 24 horas, antes de que de
 * tiempo a revisarla; y una destacada entera tiene muchos clips, asi que el
 * revisor tiene que saber cual es.
 *
 * El enlace de compartir de una destacada es
 * /s/<base64 de "highlight:<id>">?story_media_id=<clip>_<cuenta>. Devuelve
 * null si el enlace no es de una historia, para seguir con las publicaciones.
 */
function parseInstagramStory(url: URL, segments: string[]): VideoUrlResult | null {
  const first = segments[0]?.toLowerCase();

  if (first === 'stories') {
    const highlight = segments[1]?.toLowerCase() === 'highlights';
    return { ok: false, error: highlight ? 'STORY_CLIP_MISSING' : 'STORY_EXPIRES' };
  }
  if (first !== 's') return null;

  const code = segments[1] ?? '';
  if (!/^highlight:\d+$/.test(decodeBase64(code) ?? '')) {
    return { ok: false, error: 'UNSUPPORTED_PATH' };
  }

  const clip = url.searchParams.get('story_media_id') ?? '';
  const media = /^(\d+)(?:_\d+)?$/.exec(clip)?.[1];
  if (!media) return { ok: false, error: 'STORY_CLIP_MISSING' };

  // Se conserva `stkn`, el token con el que Instagram comparte el clip: sin
  // poder abrirlo con sesion no hay garantia de que el enlace funcione sin el.
  // Todo lo demas se descarta.
  const normalized = new URL(`https://www.instagram.com/s/${code}`);
  normalized.searchParams.set('story_media_id', clip);
  const token = url.searchParams.get('stkn');
  if (token) normalized.searchParams.set('stkn', token);

  return {
    ok: true,
    value: {
      platform: 'INSTAGRAM',
      normalizedUrl: normalized.toString(),
      // El clip, no la destacada: dos clips de la misma no son un duplicado.
      postExternalId: `instagram:story:${media}`,
      isShortLink: false,
      authorHandle: null,
      isStory: true,
    },
  };
}

function parseInstagram(url: URL): VideoUrlResult {
  const segments = url.pathname.split('/').filter(Boolean);

  const story = parseInstagramStory(url, segments);
  if (story) return story;

  // Formatos: /reel/{code}, /p/{code}, /tv/{code} y /{usuario}/reel/{code}
  const typeIndex = segments.findIndex((s) => INSTAGRAM_PATHS.includes(s.toLowerCase()));
  if (typeIndex === -1) return { ok: false, error: 'UNSUPPORTED_PATH' };

  const type = segments[typeIndex].toLowerCase();
  const code = segments[typeIndex + 1];
  const handle = typeIndex > 0 ? segments[0] : null;

  if (!code || !/^[A-Za-z0-9_-]{5,}$/.test(code)) {
    return { ok: false, error: 'MISSING_POST_ID' };
  }

  // Instagram sirve /reels/ y /reel/ indistintamente; se canoniza a /reel/.
  const canonicalType = type === 'reels' ? 'reel' : type;

  return {
    ok: true,
    value: {
      platform: 'INSTAGRAM',
      normalizedUrl: `https://www.instagram.com/${canonicalType}/${code}/`,
      // El shortcode identifica el post con independencia de la ruta usada,
      // asi dos rutas distintas al mismo video se detectan como duplicado.
      postExternalId: `instagram:${code}`,
      isShortLink: false,
      authorHandle: handle,
      isStory: false,
    },
  };
}

/** Quita los parametros de seguimiento de una URL cualquiera (RF-VID-02). */
export function stripTrackingParams(rawUrl: string): string {
  try {
    const url = new URL(rawUrl);
    for (const param of TRACKING_PARAMS) url.searchParams.delete(param);
    url.hash = '';
    return url.toString();
  } catch {
    return rawUrl;
  }
}

/**
 * URL del reproductor embebido oficial (RF-VID-05).
 * Se usa en un iframe con sandbox; CaliPeak no toca el archivo de video.
 */
export function embedUrlFor(parsed: ParsedVideoUrl): string | null {
  // Instagram no ofrece reproductor embebido para historias.
  if (parsed.isStory) return null;
  if (parsed.platform === 'TIKTOK') {
    const id = parsed.postExternalId.replace(/^tiktok:/, '');
    if (parsed.isShortLink) return null; // hay que resolver el enlace antes
    return `https://www.tiktok.com/embed/v2/${id}`;
  }
  const code = parsed.postExternalId.replace(/^instagram:/, '');
  return `https://www.instagram.com/p/${code}/embed/`;
}

/** Hashtag de la competicion, normalizado (RF-VID-06). */
export function normalizeHashtag(value: string): string {
  const clean = value.trim().replace(/^#+/, '').replace(/[^A-Za-z0-9_]/g, '');
  return clean ? `#${clean}` : '';
}
